import React, { useContext, useEffect } from 'react'
import { useSearchParams } from "react-router-dom";
import {
    TextField, Checkbox,
    FormControlLabel, FormControl,
    Divider, Autocomplete, Button, CircularProgress
} from '@mui/material'
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { countRequest } from '../../API/resultAPI'
import { Context } from '../../AppContext';

import { europeCitiesEN } from '../../cities/europeCitiesEN'
import { europeCitiesRU } from '../../cities/europeCitiesRU'
import { russianCitiesRU } from '../../cities/russianCitiesRU'
import { russianCitiesEN } from '../../cities/russianCitiesEN'

const MainForm = ({
    totalVolume, totalKg, valueFrom, setValueFrom,
    valueTo, setValueTo, setModalTitle
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const defLang = searchParams.get("current-locale")
    const defTo = searchParams.get("text-to")
    const defFrom = searchParams.get("text-from")

    const lang = localStorage.getItem('i18nextLng')
    const { t } = useTranslation()

    const { inputFields, setInputFields,
        setLoading, setTotalCount, setOpenModal, takeOfSender,
        setTakeOfSender, delivery, setDelivery,
        insurance, setInsurance, loading, cities,
        setCities, russianCities, setRussianCities,
        codeFrom, setCodeFrom,
        codeTo, setCodeTo, error, setError
    } = useContext(Context)

    useEffect(() => {
        if (lang === 'ru') {
            setCities(europeCitiesRU)
            setRussianCities(russianCitiesRU)
        }
        else {
            setCities(europeCitiesEN)
            setRussianCities(russianCitiesEN)
        }
    }, [lang, setCities, setRussianCities])

    const matchResult = (from, to) => {
        const f = cities.filter(el => el.label === from)
        const tt = russianCities.filter(el => el.label === to)

        return [f[0], tt[0]]
    }

    useEffect(() => {
        if (cities) {
            setValueFrom(defFrom || '')
            setValueTo(defTo || '')

            const res = matchResult(defFrom, defTo)

            setCodeFrom(res[0] || '')
            setCodeTo(res[1] || '')
        }
    }, [lang, cities, setValueTo, setValueFrom, setCodeFrom, setCodeTo])

    const handleChange = (index, e) => {
        const values = [...inputFields]
        values[index][e.target.name] = e.target.value
        setInputFields(values)
    }

    const addFields = () => {
        setInputFields([...inputFields, {
            length: '',
            width: '',
            height: '',
            weight: '',
            seats: ''
        }])
    }

    const handleRemoveFields = (id) => {
        const values = [...inputFields]
        values.splice(values.findIndex(value => value.id === id), 1)
        setInputFields(values)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        const data = {
            countryCodeFrom: codeFrom ? codeFrom?.country_code : "",
            postCodeFrom: (codeFrom && codeFrom.post_code) ? Number(codeFrom?.post_code?.slice(0, 2)) : "",
            postCodeTo: codeTo ? Number(codeTo.post_code) : "",
            cityFrom: valueFrom,
            cityTo: valueTo,
            totalKg: totalKg,
            totalVolume: totalVolume,
            locale: lang
        }
        try {
            const result = await countRequest(data, lang)
            setLoading(false)
            setOpenModal(true)
            setTotalCount(result)
            setModalTitle(result)
        } catch (e) {
            setError(e.message.split(" ").pop() !== '400')
            setOpenModal(true)
            setLoading(false)
            setTotalCount('')
            setModalTitle(t("warning.individually_offer"))
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="content__info">
               <h5> {t("info.general_info")} </h5>
                <div>
                    <div className="place-block">
                        <div className="place-block__form">
                            <FormControl
                                className="place-block__input">
                                <Autocomplete
                                    freeSolo
                                    onChange={(event, newValue) => { setCodeFrom(newValue) }}
                                    onInputChange={(event, newInputValue) => {
                                        setValueFrom(newInputValue)
                                    }}
                                    value={valueFrom}
                                    options={cities}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            placeholder={`${t("info.from")}`}
                                        />}
                                />
                            </FormControl>

                            <FormControl
                                className="place-block__input">
                                <Autocomplete
                                    freeSolo
                                    onChange={(event, newValue) => { setCodeTo(newValue) }}
                                    value={valueTo}
                                    onInputChange={(event, newInputValue) => {
                                        setValueTo(newInputValue);
                                    }}
                                    options={russianCities}
                                    renderInput={(params) =>
                                        <TextField
                                            {...params}
                                            placeholder={`${t("info.to")}`}
                                        />}
                                />
                            </FormControl>
                        </div>
                        <div className="place-block__options">
                            <FormControlLabel
                                control={<Checkbox
                                    defaultChecked={takeOfSender}
                                    onChange={() => setTakeOfSender(!takeOfSender)}
                                />}
                                label={`${t("check.pick")}`} />
                            <FormControlLabel
                                control={<Checkbox
                                    defaultChecked={delivery}
                                    onChange={() => setDelivery(!delivery)}
                                />}
                                label={`${t("check.deliver")}`} />
                        </div>
                    </div>
                    
                    {valueFrom && valueTo && (
                        <>
                            <Divider />
                            <h5> {t("cargo_places.places")} </h5>
                            <div className="place">
                                {inputFields.map((input, index) => (
                                    <div className="place-item" key={index}>
                                        <FormControl className="place__item-input">
                                            <TextField
                                                required
                                                name={'length'}
                                                type="number"
                                                placeholder="0-25000"
                                                value={input.length}
                                                onChange={e => handleChange(index, e)}
                                                label={`${t("cargo_places.length")}`}
                                                InputProps={{ inputProps: { min: 1, max: 25000 } }}
                                            />
                                        </FormControl>
                                        <FormControl className="place__item-input">
                                            <TextField
                                                required
                                                name={'width'}
                                                type="number"
                                                placeholder="0-25000"
                                                value={input.width}
                                                onChange={e => handleChange(index, e)}
                                                label={`${t("cargo_places.width")}`}
                                                InputProps={{ inputProps: { min: 1, max: 25000 } }}
                                            />
                                        </FormControl>
                                        <FormControl className="place__item-input">
                                            <TextField
                                                required
                                                name={'height'}
                                                type="number"
                                                placeholder="0-25000"
                                                value={input.height}
                                                onChange={e => handleChange(index, e)}
                                                label={`${t("cargo_places.height")}`}
                                                InputProps={{ inputProps: { min: 1, max: 25000 } }}
                                            />
                                        </FormControl>
                                        <FormControl className="place__item-input">
                                            <TextField
                                                name={'weight'}
                                                type="number"
                                                placeholder="0-25000"
                                                value={input.weight}
                                                onChange={e => handleChange(index, e)}
                                                label={`${t("cargo_places.weight")}`}
                                                InputProps={{ inputProps: { min: 1, max: 25000 } }}
                                            />
                                        </FormControl>
                                        <FormControl className="place__item-input">
                                            <TextField
                                                required
                                                name={'seats'}
                                                type="number"
                                                placeholder="0-25000"
                                                value={input.seats}
                                                onChange={e => handleChange(index, e)}
                                                label={`${t("cargo_places.seats")}`}
                                                InputProps={{ inputProps: { min: 1, max: 25000 } }}
                                            />
                                        </FormControl>
                                        {inputFields.length > 1 &&
                                            <Button variant="text"
                                                onClick={() => handleRemoveFields(input.id)}>
                                                <DeleteForeverOutlinedIcon />
                                            </Button>
                                        }
                                    </div>
                                ))}
                                <div className="place-block__add">
                                    <button onClick={addFields}>
                                        <AddIcon
                                        /> {t("button.add_place")}
                                    </button>
                                </div>
                            </div>
                            <Divider />
                            <div className="insurance-block">
                                <FormControlLabel control={<Checkbox value={insurance} onChange={() => setInsurance(!insurance)} />} label={t("check.insurance")} />
                                <FormControlLabel control={<Checkbox required={true} />} label={t("check.personal_data")}/>
                            </div>
                        </>
                    )}
                </div>
                <div className="calculate-block">
                    {(valueFrom && valueTo) && (
                        <button
                            disabled={loading}
                            className="calculate-block__btn"
                            type="submit">
                            {loading ?
                                <CircularProgress size={30} sx={{ color: 'white' }} /> :
                                t("button.calculate")
                            }
                        </button>
                    )}
                </div>
            </div>
        </form>
    )
}

export default MainForm

